import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Radio,
  RadioGroup,
  Typography,
  Stack,
} from "@mui/material";

const RESULTS = {
  result1: {
    id: "result1",
    label: "Do not buy anything.",
    description: "You indicated that you are not in a position to spend money.",
    price: 0
  },
  result2: {
    id: "result2",
    label: "$5 NC Bundle (500NC)",
    description: "Seems that you only really need 1 or 2 items from the mall as a one time purchase. This smaller bundle might be enough to cover everything!",
    price: 5
  },
  result3: {
    id: "result3",
    label: "$10 NC Bundle (1,000NC)",
    description: "This bundle is great for a small pick me up. It's not enough to go on a crazy shopping spree, but perfect if you're looking for just a quick couple items. It also comes with two boxes in case you wanted to trade or gift a friend!",
    price: 10
  },
  result4: {
    id: "result4",
    label: "$20 NC Bundle (2,200NC)",
    description: (<>
      One of the more popular bundles amongst NC regulars. This comes with 4 boxes and a trendsetter cap! 2,200NC will be enough for moderate spending. You can get quite a few wearables with this, maybe a cap bundle, or a few tokens!
      <br /><br />

      Note: "few tokens" imply prismatics/SSS, which do not guarantee specific results. If you are looking to buy multiple DSSS to obtain tokens, you will likely need a higher bundle as the regular cost of 1 DSSS is 1,400NC.
    </>),
    price: 20
  },
  result5: {
    id: "result5",
    label: "$50 NC Bundle (6,000NC)",
    description: "This comes with 5 boxes and 2 trendsetter caps! With this bundle, you can go a decent spending spree. You can stock up on cookies, get couple a wearables, some tokens, and maybe a bundle or two! Great for still getting a decent amount of NC without having to commit to the big $100 bundle.",
    price: 50
  },
  result6: {
    id: "result6",
    label: "$100 NC Bundle (14,000NC)",
    description: ("The ultimate bundle! You get 8 boxes and 3 trendsetter caps with this. The world is your oyster with this bundle - you can buy anything you want in the mall. This bundle is more likely to be recommended to those who indicate that they want to buy more, especially if it comes to tokens. Since you're buying in bulk, this will be the best NC deal (cost of NC per dollar) that you can get. Spend it, or save some in the future to have on hand! There's a lot you can do with that much NC!"),
    price: 100
  }
};

const FALLBACK_ORDER = ["result2", "result3", "result4", "result5", "result6"];

const EXTRA_TEXTS = {
  extra1:
    "You indicated on your quiz that you like a good deal. There's usually a cookie sale on Black Friday. Depending on when you took this quiz, you might want to consider waiting if it's close to November!",
  extra2: "Note: Your result may be a higher bundle recommendation due to your token goals. They can get pretty pricey!",
  extra3: "You indicated that you may be new to trading - welcome to trading! Have you checked our beginner's guide?",
};

const QUIZ_FLOW = {
  q1: {
    id: "q1",
    type: "single",
    question: "Are you in a position to spend money?",
    options: [
      { id: "q1_a", label: "Yes", next: "q2" },
      { id: "q1_b", label: "No", next: null, lean: ["result1"] },
    ],
  },

  q2: {
    id: "q2",
    type: "multi",
    question: "What's your goal in purchasing NC? (select all that apply)",
    options: [
      { id: "q2_a", label: "I want perks like cookies" },
      { id: "q2_b", label: "I want some current wearables (direct buyables)" },
      { id: "q2_c", label: "I want tokens" },
      { id: "q2_d", label: "I want to buy fun stuff in the mall for current events (caps, event tickets, dyeworks, etc)" },
    ],
  },

  q3a: {
    id: "q3a",
    type: "single",
    question: "Do you want/need to stock up on smaller perk items like cookies?",
    options: [
      {
        id: "q3a_a",
        label: "No, this is more of a one time purchase. I need a few at most.",
        lean: ["result2", "result3"],
        price: { min: 2, max: 6 },
      },
      {
        id: "q3a_b",
        label: "I don't have to, but I would if there's a good deal.",
        lean: ["result2", "result3", "result4"],
        price: { min: 2, max: 20 },
        extraText: "extra1",
      },
      {
        id: "q3a_c",
        label: "Yes! I need at least 10 cookies right now!",
        lean: ["result4", "result5"],
        price: { min: 10, max: 30 },
      },
    ],
  },

  q3b: {
    id: "q3b",
    type: "single",
    question: "How many items are on your wishlist/shopping list?",
    options: [
      {
        id: "q3b_a",
        label: "I just want one item.",
        lean: ["result2"],
        price: { min: 2, max: 5 },
      },
      {
        id: "q3b_b",
        label: "Maybe a couple items, but nothing crazy. (less than 5)",
        lean: ["result3"],
        price: { min: 5, max: 10 },
      },
      {
        id: "q3b_c",
        label: "A moderate amount. Maybe 5-7 items.",
        lean: ["result3", "result4"],
        price: { min: 10, max: 25 },
      },
      {
        id: "q3b_d",
        label: "I want to buy a lot!",
        lean: ["result4", "result5", "result6"],
        price: { min: 5, max: 30 },
      },
    ],
  },

  q4a: {
    id: "q4a",
    type: "single",
    question: "How many tokens do you want?",
    options: [
      { id: "q4a_a", label: "Just one!", lean: ["result3", "result4"], next: "q4b" },
      { id: "q4a_b", label: "A couple.", lean: ["result4", "result5"], next: "q4b" },
      {
        id: "q4a_c",
        label: "A lot!",
        lean: ["result5", "result6"],
        lock: ["result3", "result4"],
        extraText: "extra2",
        next: "q4b",
      },
    ],
  },

  q4b: {
    id: "q4b",
    type: "single",
    question: "Are all the token(s) you want in this current rotation?",
    options: [
      { id: "q4b_a", label: "Yes", next: "q4c" },
      { id: "q4b_b", label: "No", next: "q5a" },
    ],
  },

  q4c: {
    id: "q4c",
    type: "single",
    question: "Do you want/need to gamble for the style(s) you want?",
    options: [
      { id: "q4c_a", label: "No, I don't need to. The pool for my species is guaranteed." },
      { id: "q4c_b", label: "Not really. I prefer to spend more to get a guaranteed result." },
      { id: "q4c_c", label: "Yes, bring it on!" },
    ],
  },

  q5a: {
    id: "q5a",
    type: "single",
    question: "Do you plan on trading? How much?",
    options: [
      { id: "q5a_a", label: "No. I'm not a trader.", next: "q6" },
      { id: "q5a_b", label: "Yes - Just casually", next: "q5b" },
      { id: "q5a_c", label: "Yes - I trade frequently", next: "q5b" },
      {
        id: "q5a_d",
        label: "Yes - I need to since my items aren't in the mall but I'm new so not sure how much I need to trade.",
        next: "q5b",
        extraText: "extra3",
      },
    ],
  },

  q5b: {
    id: "q5b",
    type: "single",
    question: "Do you have enough boxes to trade?",
    options: [
      { id: "q5b_a", label: "Yes. I have enough so I'm not worried.", next: "q6" },
      {
        id: "q5b_b",
        label: "I have some boxes, but I could use more!",
        next: "q6",
        price: { min: 2, max: 2 },
        lock: ["result2"],
      },
      {
        id: "q5b_c",
        label: "No, I always need more boxes.",
        next: "q6",
        price: { min: 4, max: 10 },
        lock: ["result2", "result3"],
      },
    ],
  },

  q6: {
    id: "q6",
    type: "single",
    question: "Final question! Do you care for the trendsetter cap items?",
    options: [
      { id: "q6_a", label: "No" },
      { id: "q6_b", label: "Nice to have, but not a deal breaker", lean: ["result4", "result5", "result6"] },
      { id: "q6_c", label: "Yes, I want at least one trendsetter cap included.", lock: ["result2", "result3"] },
    ],
  },
};

/* --- helpers --- */
function sumRange(acc, r) {
  if (!r) return acc;
  return [acc[0] + (r.min || 0), acc[1] + (r.max || r.min || 0)];
}

// function rangeToString([min, max]) {
//   if (min === max) return `$${min}`;
//   return `$${min}-${max}`;
// }

/* --- component --- */

export default function ProductRecommendationQuiz() {
  const [currentQueue, setCurrentQueue] = useState(["q1"]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [leanScores, setLeanScores] = useState({});
  const [locked, setLocked] = useState(new Set());
  const [priceRanges, setPriceRanges] = useState([]);
  const [extraTextKeys, setExtraTextKeys] = useState(new Set());
  const [showResult, setShowResult] = useState(false);
  const [finalResult, setFinalResult] = useState(null);

  const currentQid = currentQueue[currentIndex];
  const currentQ = QUIZ_FLOW[currentQid];

  const applyOptionEffects = (opt) => {
    if (!opt) return;
    if (opt.lean) {
      setLeanScores((prev) => {
        const next = { ...prev };
        opt.lean.forEach((r) => {
          next[r] = (next[r] || 0) + 1;
        });
        return next;
      });
    }
    if (opt.lock) {
      setLocked((prev) => {
        const next = new Set(prev);
        opt.lock.forEach((r) => next.add(r));
        return next;
      });
    }
    if (opt.price) {
      setPriceRanges((prev) => [...prev, opt.price]);
    }
    if (opt.extraText) {
      setExtraTextKeys((prev) => new Set([...Array.from(prev), opt.extraText]));
    }
  };

  /* ---------- FIXED handleSingleNext ----------
     Behavior:
     - Apply option effects.
     - If option specifies opt.next, insert/replace next and advance to it.
     - Else if there's already another question queued after this one, advance to the queued next.
     - Otherwise finish and show results.
  */
  const handleSingleNext = (qid) => {
    const selectedId = answers[qid];
    if (!selectedId) return;

    const q = QUIZ_FLOW[qid];
    const opt = q.options.find((o) => o.id === selectedId);
    if (!opt) return;

    applyOptionEffects(opt);

    if (opt.next) {
      // ensure the specified next question is placed right after current, but DO NOT truncate the rest
      setCurrentQueue((prev) => {
        const newQueue = [...prev];
        if (currentIndex + 1 < newQueue.length) {
          newQueue[currentIndex + 1] = opt.next;
        } else {
          newQueue.push(opt.next);
        }
        return newQueue;
      });
      setCurrentIndex((i) => i + 1);
      return;
    }

    // No explicit next: move to already queued next question if it exists
    if (currentIndex + 1 < currentQueue.length) {
      setCurrentIndex((i) => i + 1);
      return;
    }

    // Otherwise end quiz
    setCurrentQueue((prev) => prev.slice(0, currentIndex + 1));
    setShowResult(true);
  };

  const handleMultiSubmit = (qid, selectedIds) => {
    const q = QUIZ_FLOW[qid];
    setAnswers((prev) => ({ ...prev, [qid]: [...selectedIds] }));

    // Apply effects for selected options
    selectedIds.forEach((optId) => {
      const opt = q.options.find((o) => o.id === optId);
      if (opt) applyOptionEffects(opt);
    });

    // q2 special behavior: queue follow-ups
    if (qid === "q2") {
      if (selectedIds.length === 1 && selectedIds[0] === "q2_c") {
        setLocked((prev) => new Set([...Array.from(prev), "result2"]));
      }
      if (selectedIds.length > 1) {
        setLocked((prev) => new Set([...Array.from(prev), "result2"]));
      }

      const selectedSet = new Set(selectedIds);
      const newQueue = ["q1", "q2"];
      if (selectedSet.has("q2_a")) newQueue.push("q3a");
      if (selectedSet.has("q2_b") || selectedSet.has("q2_d")) newQueue.push("q3b");
      if (selectedSet.has("q2_c")) newQueue.push("q4a");
      // Always ensure q5a after followups
      if (!newQueue.includes("q5a")) newQueue.push("q5a");

      setCurrentQueue(newQueue);
      // move to first follow-up which is at index 2
      setCurrentIndex(2);
      return;
    }

    // Normal progression
    if (currentIndex + 1 < currentQueue.length) {
      setCurrentIndex((i) => i + 1);
    } else {
      setShowResult(true);
    }
  };

  useEffect(() => {
    if (!showResult) return;

    const derivedLean = { ...leanScores };
    const derivedLocked = new Set(Array.from(locked));
    const derivedPriceRanges = [...priceRanges];
    const derivedExtra = new Set(Array.from(extraTextKeys));

    // q2 re-locking defensively
    const q2Ans = answers["q2"];
    if (Array.isArray(q2Ans)) {
      if (q2Ans.length === 1 && q2Ans[0] === "q2_c") derivedLocked.add("result2");
      if (q2Ans.length > 1) derivedLocked.add("result2");
    }

    // q3a + q3b special rule
    if (answers["q3a"] === "q3a_c" && ["q3b_b", "q3b_c", "q3b_d"].includes(answers["q3b"])) {
      derivedLocked.add("result3");
    }

    // 🔹 q4a + q4b logic
    const q4aAns = answers["q4a"];
    const q4bAns = answers["q4b"];
    if (q4aAns && q4bAns === "q4b_b") {
      if (q4aAns === "q4a_a") {
        derivedLean["result4"] = (derivedLean["result4"] || 0) + 1;
        derivedLean["result5"] = (derivedLean["result5"] || 0) + 1;
        derivedPriceRanges.push({ min: 14, max: 14 });
      }
      if (q4aAns === "q4a_b") {
        derivedLean["result5"] = (derivedLean["result5"] || 0) + 1;
        derivedLean["result6"] = (derivedLean["result6"] || 0) + 1;
        derivedPriceRanges.push({ min: 40, max: 70 });
      }
      if (q4aAns === "q4a_c") {
        derivedLean["result5"] = (derivedLean["result5"] || 0) + 1;
        derivedLean["result6"] = (derivedLean["result6"] || 0) + 1;
        derivedPriceRanges.push({ min: 50, max: 80 });
      }
    }

    // 🔹 q4a + q4c logic (existing block)
    const q4cAns = answers["q4c"];
    if (q4aAns && q4cAns) {
      const comboKey = `${q4aAns}_${q4cAns}`;
      const mapping = {
        "q4a_a_q4c_a": { lean: ["result3", "result4"], price: { min: 6, max: 6 } },
        "q4a_a_q4c_b": { lean: ["result4"], price: { min: 14, max: 14 } },
        "q4a_a_q4c_c": { lean: ["result4", "result5"], price: { min: 6, max: 24 }, extra: "extra2" },

        "q4a_b_q4c_a": { lean: ["result4", "result5"], price: { min: 16, max: 28 } },
        "q4a_b_q4c_b": { lean: ["result5", "result6"], price: { min: 40, max: 70 } },
        "q4a_b_q4c_c": { lean: ["result5", "result6"], price: { min: 40, max: 70 } },

        "q4a_c_q4c_a": { lean: ["result5", "result6"], price: { min: 45, max: 45 } },
        "q4a_c_q4c_b": { lean: ["result6"], price: { min: 60, max: 100 } },
        "q4a_c_q4c_c": { lean: ["result6"], price: { min: 60, max: 100 } },
      };

      const cfg = mapping[comboKey];
      if (cfg) {
        if (cfg.lean) cfg.lean.forEach((r) => (derivedLean[r] = (derivedLean[r] || 0) + 1));
        if (cfg.price) derivedPriceRanges.push(cfg.price);
        if (cfg.extra) derivedExtra.add(cfg.extra);
      }
    }

    // combined price
    const combined = derivedPriceRanges.reduce((acc, r) => sumRange(acc, r), [0, 0]);
    const totalMin = combined[0];

    // pick result
    const entries = Object.entries(derivedLean).sort((a, b) => {
      const scoreDiff = b[1] - a[1];
      if (scoreDiff !== 0) return scoreDiff;
      const ia = FALLBACK_ORDER.indexOf(a[0]);
      const ib = FALLBACK_ORDER.indexOf(b[0]);
      return ia - ib;
    });

    let finalKey = null;
    for (let [key] of entries) {
      if (!derivedLocked.has(key)) {
        finalKey = key;
        break;
      }
    }

    if (!finalKey) {
      for (let fk of FALLBACK_ORDER) {
        if (!derivedLocked.has(fk)) {
          finalKey = fk;
          break;
        }
      }
    }
    if (!finalKey) finalKey = "result1";

    // 🔹 final bump check with special rules
    const currentPrice = RESULTS[finalKey].price;

    if (finalKey === "result4") {
      if (totalMin >= currentPrice + 10) {
        const idx = FALLBACK_ORDER.indexOf(finalKey);
        if (idx !== -1 && idx < FALLBACK_ORDER.length - 1) {
          finalKey = FALLBACK_ORDER[idx + 1];
        }
      }
    } else if (finalKey === "result5") {
      if (totalMin >= currentPrice + 20) {
        const idx = FALLBACK_ORDER.indexOf(finalKey);
        if (idx !== -1 && idx < FALLBACK_ORDER.length - 1) {
          finalKey = FALLBACK_ORDER[idx + 1];
        }
      }
    } else {
      if (totalMin > currentPrice) {
        const idx = FALLBACK_ORDER.indexOf(finalKey);
        if (idx !== -1 && idx < FALLBACK_ORDER.length - 1) {
          finalKey = FALLBACK_ORDER[idx + 1];
        }
      }
    }


    setLeanScores(derivedLean);
    setLocked(derivedLocked);
    setPriceRanges(derivedPriceRanges);
    setExtraTextKeys(derivedExtra);
    setFinalResult(finalKey);

    // console.log("=== QUIZ DEBUG SUMMARY ===");
    // console.log("Answers:", answers);
    // console.log("Derived locked:", Array.from(derivedLocked));
    // console.log("Derived lean scores:", derivedLean);
    // console.log("Accumulated internal price range (dev):", rangeToString(combined));
    // console.log("Final computed result (returned to user):", finalKey);
    // console.log("Extra text flags:", Array.from(derivedExtra));
    // console.log("==========================");
    // eslint-disable-next-line
  }, [showResult]);


  const renderSingle = (q) => {
    const selected = answers[q.id] || "";
    return (
      <>
        <RadioGroup
          value={selected}
          onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
        >
          {q.options.map((opt) => (
            <FormControlLabel key={opt.id} value={opt.id} control={<Radio />} label={opt.label} sx={{ my: 1 }} />
          ))}
        </RadioGroup>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" disabled={!answers[q.id]} onClick={() => handleSingleNext(q.id)}>
            Next
          </Button>

          {currentIndex > 0 && (
            <Button variant="outlined" onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}>
              Back
            </Button>
          )}
        </Stack>
      </>
    );
  };

  const renderMulti = (q) => {
    const selectedSet = new Set(answers[q.id] || []);
    const toggle = (id) => {
      setAnswers((prev) => {
        const cur = new Set(prev[q.id] || []);
        if (cur.has(id)) cur.delete(id);
        else cur.add(id);
        return { ...prev, [q.id]: Array.from(cur) };
      });
    };

    return (
      <>
        <FormControl component="fieldset">
          <FormGroup>
            {q.options.map((opt) => (
              <FormControlLabel
                key={opt.id}
                control={<Checkbox checked={selectedSet.has(opt.id)} onChange={() => toggle(opt.id)} name={opt.id} />}
                label={opt.label}
                sx={{ my: 1 }}
              />
            ))}
          </FormGroup>
        </FormControl>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            disabled={!answers[q.id] || answers[q.id].length === 0}
            onClick={() => handleMultiSubmit(q.id, answers[q.id] || [])}
          >
            Next
          </Button>

          {currentIndex > 0 && (
            <Button variant="outlined" onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}>
              Back
            </Button>
          )}
        </Stack>
      </>
    );
  };

  if (showResult) {
    if (!finalResult) {
      return (
        <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6">Computing recommendation…</Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {RESULTS[finalResult].label}
          </Typography>

          {Array.from(extraTextKeys).map((k) => (
            <Typography key={k} variant="body2" sx={{ mb: 1 }}>
              {EXTRA_TEXTS[k]}
            </Typography>
          ))}

          <Typography variant="body2" sx={{ mt: 2 }}>
            {RESULTS[finalResult].description}
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={() => {
                setCurrentQueue(["q1"]);
                setCurrentIndex(0);
                setAnswers({});
                setLeanScores({});
                setLocked(new Set());
                setPriceRanges([]);
                setExtraTextKeys(new Set());
                setShowResult(false);
                setFinalResult(null);
              }}
            >
              Retake Quiz
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {currentQ ? `Question: ${currentQ.question}` : "No more questions"}
        </Typography>

        <LinearProgress variant="determinate" value={100} sx={{ mb: 2, borderRadius: 1 }} />

        <Box>{currentQ ? (currentQ.type === "single" ? renderSingle(currentQ) : renderMulti(currentQ)) : <Button onClick={() => setShowResult(true)}>See results</Button>}</Box>

        {/* <Box sx={{ mt: 3 }}>
          <Typography variant="caption">Debug: question queue: {currentQueue.join(" → ")}</Typography>
        </Box> */}
      </CardContent>
    </Card>
  );
}
